import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Activity as ActivityIcon, User as UserIcon } from 'lucide-react';
import { api, unwrap } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';

interface ActivityEntry {
  id: number;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  resource_label: string | null;
  meta: Record<string, unknown> | null;
  ip: string | null;
  created_at: string;
  user: { id: number; name: string; email: string } | null;
}

interface Paginated<T> { data: T[]; total: number; current_page: number; last_page: number }

export const Route = createFileRoute('/_app/activity')({
  component: ActivityView,
});

const ACTION_COLORS: Record<string, string> = {
  created: 'bg-green-100 text-green-700',
  updated: 'bg-blue-100 text-blue-700',
  deleted: 'bg-red-100 text-red-700',
  published: 'bg-emerald-100 text-emerald-700',
  duplicated: 'bg-purple-100 text-purple-700',
  imported: 'bg-amber-100 text-amber-700',
  restored: 'bg-indigo-100 text-indigo-700',
  activated: 'bg-cyan-100 text-cyan-700',
};

const RESOURCE_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Todos los recursos' },
  { value: 'page', label: 'Páginas' },
  { value: 'post', label: 'Contenidos' },
  { value: 'media', label: 'Multimedia' },
  { value: 'user', label: 'Usuarios' },
  { value: 'form', label: 'Formularios' },
  { value: 'theme', label: 'Tema' },
  { value: 'pattern', label: 'Patrones' },
  { value: 'redirect', label: 'Redirecciones' },
];

function ActivityView() {
  const [page, setPage] = useState(1);
  const [resourceType, setResourceType] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['activity-log', page, resourceType],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), per_page: '50' });
      if (resourceType) params.set('resource_type', resourceType);
      return unwrap<Paginated<ActivityEntry>>(api.get(`/activity-log?${params}`));
    },
    refetchInterval: 30_000,
  });

  return (
    <div className="mx-auto max-w-7xl p-6 md:p-8">
      <header className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Actividad reciente</h1>
        <p className="text-sm text-neutral-500">Audit log de todas las acciones del admin.</p>
      </header>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>{data?.total ?? 0} eventos</CardTitle>
              <CardDescription>Auto-actualización cada 30s.</CardDescription>
            </div>
            <select
              value={resourceType}
              onChange={(e) => { setResourceType(e.target.value); setPage(1); }}
              className="h-9 rounded-md border border-neutral-200 px-3 text-sm"
            >
              {RESOURCE_FILTERS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && <div className="py-8 text-center text-sm text-neutral-400">Cargando…</div>}
          <ul className="divide-y divide-neutral-100">
            {(data?.data ?? []).map((e) => {
              const verb = e.action.split('.').pop() ?? '';
              const color = ACTION_COLORS[verb] ?? 'bg-neutral-100 text-neutral-700';
              return (
                <li key={e.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="grid size-8 shrink-0 place-items-center rounded-full bg-neutral-100">
                    <ActivityIcon className="size-4 text-neutral-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Badge className={color}>{e.action}</Badge>
                      {e.resource_type && (
                        <span className="text-neutral-500">en {e.resource_type}</span>
                      )}
                      {e.resource_label && (
                        <span className="font-medium truncate">{e.resource_label}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-400">
                      {e.user && (
                        <span className="inline-flex items-center gap-1">
                          <UserIcon className="size-3" />
                          {e.user.name}
                        </span>
                      )}
                      <span>{new Date(e.created_at).toLocaleString('es')}</span>
                      {e.ip && <span>· {e.ip}</span>}
                    </div>
                  </div>
                </li>
              );
            })}
            {data?.data.length === 0 && !isLoading && (
              <li className="px-4 py-8 text-center text-sm text-neutral-400">Sin actividad registrada todavía.</li>
            )}
          </ul>
          {data && data.last_page > 1 && (
            <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-3 text-xs">
              <span>Página {data.current_page} de {data.last_page}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                <Button size="sm" variant="outline" disabled={page >= data.last_page} onClick={() => setPage(page + 1)}>Siguiente</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
